/**
 * M7 S4: immutable helpers over the Career story block (`career.story`, spec §12.1, §1 rule 11). The block is the
 * truth for every story fact; these helpers are Career-in, Career-out and never change their input.
 *
 * An absent block reads as an empty one (`storyOf`), and is only written when something actually changes, so a
 * career that never touches the story keeps no block (pre-M7 saves stay byte-identical).
 */
import type { Career } from '../career/types'
import { emptyStoryState } from './schemas'
import { STORY_FLAGS, type StoryFlag, type StoryFlags, type StoryState } from './types'

let empty: StoryState | undefined

/** The career's story block, or a shared empty one (read-only: never mutate the result). */
export function storyOf(career: Career): StoryState {
  if (career.story !== undefined) return career.story
  empty ??= emptyStoryState()
  return empty
}

/** The career with `story` as its story block (the same career when the block is unchanged). */
export function withStoryState(career: Career, story: StoryState): Career {
  if (story === career.story) return career
  return { ...career, story }
}

/** The career with `patch` applied to its story block (a fresh block when it had none). */
export function withStory(career: Career, patch: (story: StoryState) => StoryState): Career {
  const before = storyOf(career)
  const after = patch(before)
  if (after === before) return career
  return withStoryState(career, after)
}

export function hasFlag(career: Career, flag: StoryFlag): boolean {
  return storyOf(career).flags[flag] === true
}

/** Sets `flag` (the same career when it is already set). */
export function setFlag(career: Career, flag: StoryFlag): Career {
  if (hasFlag(career, flag)) return career
  return withStory(career, (s) => ({ ...s, flags: { ...s.flags, [flag]: true } }))
}

/** Sets every flag in `flags`. */
export function setFlags(career: Career, flags: readonly StoryFlag[]): Career {
  let next = career
  for (const flag of flags) next = setFlag(next, flag)
  return next
}

/** Clears `flag` (the key is removed: unset flags are never stored as `false`). */
export function clearFlag(career: Career, flag: StoryFlag): Career {
  if (!hasFlag(career, flag)) return career
  return withStory(career, (s) => {
    const flags: StoryFlags = {}
    for (const f of STORY_FLAGS) if (f !== flag && s.flags[f] === true) flags[f] = true
    return { ...s, flags }
  })
}

/** Online faction days counted so far (plan §2: a catch-up day is never online). */
export function onlineDaysOf(career: Career): number {
  return storyOf(career).onlineDays
}

/** Counts one online faction day (the caller only calls this for a day that was not simulated by catch-up). */
export function countOnlineDay(career: Career): Career {
  return withStory(career, (s) => ({ ...s, onlineDays: s.onlineDays + 1 }))
}
